import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, Link } from 'react-router-dom';
import { apiClient } from '../../apiClient';
import { useAuth } from '../../firebase';

const BillingSnapshot = () => {
    const { t } = useTranslation('billing');
    const { snapshotId } = useParams();
    const currentUser = useAuth();
    
    const [details, setDetails] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState(null);
    const [lastVisible, setLastVisible] = useState(null);
    const [hasMore, setHasMore] = useState(true);
    const [filters, setFilters] = useState({ customerId: '' });

    const fetchDetails = useCallback(async (loadMore = false) => {
        if (!currentUser) return;
        
        if (loadMore) {
            setLoadingMore(true);
        } else {
            setLoading(true);
        }
        setError(null);

        try {
            const params = new URLSearchParams();
            if (filters.customerId) params.append('customerId', filters.customerId);
            if (loadMore && lastVisible) params.append('lastVisible', lastVisible);

            const response = await apiClient.get(`/v1/billing-history/${snapshotId}?${params.toString()}`);

            if (!response.ok) {
                throw new Error(t('errors.fetchFailed'));
            }
            
            const data = await response.json();

            setDetails(prev => loadMore ? [...prev, ...data.details] : data.details);
            setLastVisible(data.lastVisible);
            setHasMore(data.details.length === 10 && data.lastVisible);

        } catch (err) {
            console.error('Fetch error:', err);
            setError(t('errors.fetchFailed'));
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [currentUser, snapshotId, filters.customerId, lastVisible, t]);

    useEffect(() => {
        fetchDetails(false);
    }, [filters.customerId, currentUser, snapshotId]);

    const handleLoadMore = () => {
        if (!loading && hasMore) {
            fetchDetails(true);
        }
    };
  
    const handleFilterChange = (e) => {
        const { value } = e.target;
        setFilters({ customerId: value });
    };

    const handleCsvDownload = async () => {
        if (!currentUser) return;
        try {
            const response = await apiClient.get(`/v1/billing-history/${snapshotId}/csv`);
            if (!response.ok) throw new Error(t('errors.csvFailed'));
            
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `billwave_usage_details_${snapshotId}.csv`;
            document.body.appendChild(a);
            a.click();
            a.remove();

        } catch (error) {
            console.error('CSV download error:', error);
            alert(t('errors.csvFailed'));
        }
    };

    return (
        <div className="bg-white p-8 w-full max-w-6xl mx-auto my-8 shadow-lg rounded-lg">
             <div className="flex justify-between items-center mb-6 pb-2 border-b-2 border-gray-200">
                <h1 className="text-3xl font-bold text-gray-900">{t('billedInvoices')}</h1>
                <Link to="/settings/billing-history" className="text-blue-600 hover:underline text-sm font-bold">戻る</Link>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg mb-6">
                <div className="flex justify-between items-end gap-6">
                    <div className="flex-grow">
                        <label className="block text-sm font-medium text-gray-700 mb-1">{t('customerId')}</label>
                        <input 
                            type="text" 
                            name="customerId" 
                            placeholder={t('customerIdFilter')} 
                            value={filters.customerId} 
                            onChange={handleFilterChange} 
                            className="block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm" 
                        />
                    </div>
                    <button onClick={handleCsvDownload} className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md whitespace-nowrap">
                        {t('csvDownload')}
                    </button>
                </div>
            </div>

            {error && <p className="text-red-500 mb-4">{error}</p>}
            
            {loading && details.length === 0 ? (
                <p className="text-center py-4">{t('loading')}</p>
            ) : details.length === 0 ? (
                <p className="text-center py-4">{t('noBilledInvoices')}</p>
            ) : (
                <>
                    <div className="overflow-x-auto">
                        <table className="w-full border-collapse table-auto">
                            <thead>
                                <tr className="bg-gray-100">
                                    <th className="border p-2 text-left whitespace-nowrap">{t('processedAt')}</th>
                                    <th className="border p-2 text-left">{t('customerId')}</th>
                                    <th className="border p-2 text-left">{t('invoiceId')}</th>
                                    <th className="border p-2 text-left">{t('totalAmount')}</th>
                                    <th className="border p-2 text-left">アクション</th>
                                </tr>
                            </thead>
                            <tbody>
                                {details.map(item => (
                                    <tr key={item.id} className="hover:bg-gray-50">
                                        <td className="border p-2 whitespace-nowrap">
                                            {item.processed_at ? (item.processed_at._seconds ? new Date(item.processed_at._seconds * 1000).toLocaleString() : new Date(item.processed_at).toLocaleString()) : '-'}
                                        </td>
                                        <td className="border p-2 break-all">{item.customer_id}</td>
                                        <td className="border p-2 font-mono text-xs break-all">{item.invoice_id}</td>
                                        <td className="border p-2">¥{item.total_amount?.toLocaleString()}</td>
                                        <td className="border p-2 whitespace-nowrap">
                                            {item.invoice_pdf && (
                                                <a href={item.invoice_pdf} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">{t('download')}</a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className="flex justify-center mt-6">
                        {hasMore && (
                            <button 
                                onClick={handleLoadMore} 
                                disabled={loadingMore} 
                                className="bg-blue-600 text-white font-bold py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors shadow-md disabled:bg-blue-400 disabled:cursor-not-allowed"
                            >
                                {loadingMore ? t('loading') : t('loadMore')}
                            </button>
                        )}
                    </div>
                </>
            )}
        </div>
    );
}

export default BillingSnapshot;
